import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { Table, Collapse } from 'antd';

import * as actions from './store/actions';

import { getTableColumns } from './table-view-layout';

import EditFormModal from './edit-form-modal';

import OriginalPdfFormsHeader from './original-pdf-forms-header';

const { Panel } = Collapse;

const defaultPageSize = 10;

export const OriginalPdfFormsComponent = ({ rootContainer, permissions, contactType, rowSelectionCallback, setRefreshOriginalListCallback }) => {
  const dispatch = useDispatch();

  const [originalPdfFormsPageRequest, setOriginalPdfFormsPageRequest] = useState({
    pageNumber: 1,
    pageSize: defaultPageSize,
    sortBy: null,
    sortDirection: null,
    filter: {},
  });
  const [columns, setColumns] = useState(null);
  const [modalState, setModalState] = useState({ visible: false, fileBytes: null });

  const originalPdfForms = useSelector((state) => state.originalPdfForms.originalPdfForms);
  const originalPdfFormsLoading = useSelector((state) => state.originalPdfForms.originalPdfFormsLoading);
  const downloadOriginalPdfFormLoading = useSelector((state) => state.originalPdfForms.downloadOriginalPdfFormLoading);
  const downloadFlattenPdfFormLoading = useSelector((state) => state.originalPdfForms.downloadFlattenPdfFormLoading);

  useEffect(() => {
    modalState.visible ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'unset');
  }, [modalState.visible]);

  useEffect(() => {
    loadData(originalPdfFormsPageRequest);

    if (typeof setRefreshOriginalListCallback === 'function') {
      setRefreshOriginalListCallback(refresh);
    }
  }, []);

  useEffect(() => {
    const allColumns = getTableColumns(rootContainer, downloadBlankOriginalForm, editOriginalForm, permissions, contactType);

    setColumns(allColumns);
  }, [permissions, contactType]);

  const loadData = (pageRequest) => {
    setOriginalPdfFormsPageRequest(pageRequest);

    dispatch(actions.getOriginalPdfForms(pageRequest));
  };

  const refresh = () => {
    const updatedPageRequest = { ...originalPdfFormsPageRequest };

    updatedPageRequest.pageNumber = 1;

    loadData(updatedPageRequest);
  };

  const downloadBlankOriginalForm = (fileId, fileName) => {
    dispatch(actions.downloadFlattenPdfForm({ formId: fileId })).then((res) => {
      let fileNameToSave = fileName;

      if (!fileNameToSave.toLowerCase().endsWith('.pdf')) {
        fileNameToSave = `${fileNameToSave}.pdf`;
      }

      saveAs(res, fileNameToSave);
    });
  };

  const editOriginalForm = (pdfForm) => {
    dispatch(actions.downloadOriginalPdfForm({ formId: pdfForm.id })).then((fileBytes) => {
      setModalState({ visible: true, fileBytes: fileBytes });
    });
  };

  const handleCloseModal = () => {
    setModalState({ visible: false, fileBytes: null });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedPageRequest = { ...originalPdfFormsPageRequest };

    if (pagination && pagination.pageSize && pagination.current) {
      updatedPageRequest.pageSize = pagination.pageSize;
      updatedPageRequest.pageNumber = pagination.current;
    }

    if (sorter && sorter.column) {
      updatedPageRequest.sortBy = sorter.field;
      updatedPageRequest.sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      updatedPageRequest.sortBy = null;
      updatedPageRequest.sortDirection = null;
    }

    loadData(updatedPageRequest);
  };

  const renderTotal = (total, range) => `${range[0]}-${range[1]} of ${total} forms`;

  const loadSelectedFormBytes = (fileId) => {
    dispatch(actions.downloadOriginalPdfForm({ formId: fileId })).then((fileBytes) => {
      if (typeof rowSelectionCallback === 'function') {
        rowSelectionCallback(fileBytes);
      }
    });
  };

  return (
    <div className="original-forms-list">
      <Collapse collapsible={false} activeKey="1">
        <Panel
          showArrow={false}
          key="1"
          collapsible={false}
          header={<OriginalPdfFormsHeader loadData={loadData} originalPdfFormsPageRequest={originalPdfFormsPageRequest} refresh={refresh} />}
        >
          <Table
            rowSelection={{
              type: 'radio',
              onChange: (selectedRowKeys, selectedRows) => {
                loadSelectedFormBytes(selectedRowKeys[0]);
              },
            }}
            size="small"
            scroll={{ x: true }}
            loading={originalPdfFormsLoading || downloadOriginalPdfFormLoading || downloadFlattenPdfFormLoading}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={originalPdfForms ? originalPdfForms.data : []}
            sortDirections={['ascend', 'descend']}
            showSorterTooltip={false}
            pagination={{
              size: 'small',
              showQuickJumper: true,
              defaultPageSize: defaultPageSize,
              pageSizeOptions: [10, 15, 25, 50, 100],
              pageSize: originalPdfFormsPageRequest.pageSize,
              current: originalPdfFormsPageRequest.pageNumber,
              total: originalPdfForms ? originalPdfForms.totalCount : 0,
              showTotal: (total, range) => renderTotal(total, range),
            }}
            onChange={handleTableChange}
          />
        </Panel>
      </Collapse>

      {modalState.visible ? (
        <EditFormModal
          isModalVisible={modalState.visible}
          closeModal={handleCloseModal}
          rootContainer={rootContainer}
          fileBytes={modalState.fileBytes}
        />
      ) : null}
    </div>
  );
};
